"use client";

import { useCallback, useMemo } from "react";
import { useFormatter } from "next-intl";
import { useCountry } from "@/config/country-context";
import {
	type CurrencyCode,
	type CurrencySettings,
	getCurrencySettings,
} from "@/config/currency-config";
import {
	AMOUNT_PLACEHOLDER,
	centsFractionDigits,
	toAmount,
} from "@/shared/amount";

type FormatCurrencyOptions = {
	/** `"auto"`, the default, shows cents only when the amount has them. */
	decimalPlaces?: number | "auto";
	/** Shown when the value is absent. Pass `"0"`-style text only when a missing
	 * amount genuinely means zero. */
	fallback?: string;
};

export type UseCurrencyResult = CurrencySettings & {
	format: (
		value: number | null | undefined,
		options?: FormatCurrencyOptions
	) => string;
};

export const useCurrency = (): UseCurrencyResult => {
	const country = useCountry();
	const formatter = useFormatter();
	const settings = useMemo(() => getCurrencySettings(country), [country]);

	const format = useCallback(
		(value: number | null | undefined, options?: FormatCurrencyOptions) => {
			const amount = toAmount(value);

			// Intl renders `null` as 0 and `undefined` as the locale's NaN text
			// ("epäluku" in Finnish), so absent values are caught up front.
			if (amount === null) {
				return options?.fallback ?? AMOUNT_PLACEHOLDER;
			}

			const option = options?.decimalPlaces ?? "auto";
			const decimalPlaces =
				option === "auto" ? centsFractionDigits(amount) : option;

			return formatter.number(amount, {
				style: "currency",
				currency: settings.code,
				minimumFractionDigits: decimalPlaces,
				maximumFractionDigits: decimalPlaces,
			});
		},
		[formatter, settings.code]
	);

	return {
		...settings,
		format,
	};
};

export type { CurrencyCode, CurrencySettings };
